
import { Ball, SimulationConfig } from '@/types';
import { resolveBallCollision } from './BallCollision';

export class PhysicsSystem {
  private static readonly HASH_SIZE = 8192;
  private static readonly MAX_BALLS = 10000;
  private gridHeads = new Int32Array(PhysicsSystem.HASH_SIZE);
  private gridNext = new Int32Array(PhysicsSystem.MAX_BALLS);

  constructor() {}

  private hashFunc(cx: number, cy: number): number {
    return ((cx * 73856093) ^ (cy * 19349663)) & (PhysicsSystem.HASH_SIZE - 1);
  }

  // Optimized Ball-Ball Collision Resolution using Spatial Hashing
  resolveBallCollisions(
      balls: Ball[], 
      config: SimulationConfig,
      onCollision?: (b1: Ball, b2: Ball, impact: number) => void
  ) {
    const count = balls.length;
    if (count === 0) return;

    // Dynamic Cell Size based on max radius to ensure large objects don't miss collisions
    const maxR = config.mode === 'SPACE_SPIRAL' ? (config.spaceSpiralBoxSize || 30) : (config.ballMaxRadius || 40);
    const cellSize = Math.max(40, maxR * 2.5);
    
    this.gridHeads.fill(-1);
    
    // Insert balls into grid
    for (let i = 0; i < count; i++) {
        const b = balls[i];
        const cellX = Math.floor(b.pos.x / cellSize);
        const cellY = Math.floor(b.pos.y / cellSize);
        const hash = this.hashFunc(cellX, cellY);
        
        this.gridNext[i] = this.gridHeads[hash];
        this.gridHeads[hash] = i;
    }

    // Check collisions within cells and neighbors
    for (let i = 0; i < count; i++) {
      const b1 = balls[i];
      const cellX = Math.floor(b1.pos.x / cellSize);
      const cellY = Math.floor(b1.pos.y / cellSize);

      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const cx = cellX + dx;
          const cy = cellY + dy;
          const hash = this.hashFunc(cx, cy);
          
          let j = this.gridHeads[hash];
          while (j !== -1) {
            if (i < j) { 
              const b2 = balls[j];
              
              // Verification: coordinate check to handle hash collisions
              if (Math.floor(b2.pos.x / cellSize) === cx && Math.floor(b2.pos.y / cellSize) === cy) {
                  // SPACE SPIRAL Logic
                  if (config.mode === 'SPACE_SPIRAL' && i > 0 && j > 0) {
                      const s1 = b1.vel.x*b1.vel.x + b1.vel.y*b1.vel.y;
                      const s2 = b2.vel.x*b2.vel.x + b2.vel.y*b2.vel.y;
                      if (s1 < 0.01 && s2 < 0.01) {
                          j = this.gridNext[j];
                          continue;
                      }
                  }

                  const restitution = config.mode === 'SPACE_SPIRAL' ? config.spaceSpiralRestitution : config.restitution;
                  const impact = resolveBallCollision(b1, b2, restitution);
                  
                  if (impact > 0 && onCollision) {
                      onCollision(b1, b2, impact);
                  }
              }
            }
            j = this.gridNext[j];
          }
        }
      }
    }
  }
}
